import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Star,
  Download,
  Heart,
  Share2,
  Eye,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Play,
  Tag,
  User,
} from 'lucide-react';
// TODO: Replace mock data with Supabase fetch using templateSlug
// Example: const { data, error } = await supabase.from('templates').select('*').eq('slug', templateSlug).single();

const MOCK_TEMPLATE = {
  id: 1,
  slug: 'business-budget-tracker-pro',
  title: 'Business Budget Tracker Pro',
  platform: 'google_sheets',
  price: 9.99,
  description: `## Overview

The **Business Budget Tracker Pro** is an all-in-one financial management spreadsheet designed for small to medium businesses. Track income, expenses, forecasts, and more with automated calculations and beautiful charts.

### What's Included

This template comes with **12 pre-built tabs** covering every aspect of business budgeting. From monthly expense tracking to annual forecasting, every sheet is interconnected and updates automatically.

### How It Works

Simply enter your data into the designated input cells (highlighted in light blue), and the spreadsheet handles the rest. Formulas, charts, and summary dashboards update in real-time to give you a clear picture of your financial health.`,
  features: [
    'Automatic monthly and yearly budget summaries',
    'Expense categorization with customizable categories',
    'Income tracking with multiple revenue streams',
    'Cash flow forecasting for up to 12 months',
    'Interactive charts and visual dashboards',
    'Tax estimation and deduction tracking',
    'Vendor and supplier payment tracking',
    'Employee payroll budget allocation',
    'Currency conversion support',
    'Print-ready reports and summaries',
  ],
  use_cases: [
    {
      title: 'Startup Financial Planning',
      description:
        'Perfect for startups needing to track burn rate, runway, and funding allocation across departments.',
    },
    {
      title: 'Freelancer Income Management',
      description:
        'Freelancers can track multiple client projects, invoices, and quarterly tax estimates in one place.',
    },
    {
      title: 'Small Business Operations',
      description:
        'Manage day-to-day operational expenses, vendor payments, and profit margins with ease.',
    },
    {
      title: 'Non-Profit Budget Oversight',
      description:
        'Track grant allocations, donor contributions, and program-specific spending transparently.',
    },
  ],
  video_url: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
  screenshots: [
    '/images/templates/budget-tracker-1.png',
    '/images/templates/budget-tracker-2.png',
    '/images/templates/budget-tracker-3.png',
  ],
  reviews: [
    {
      id: 1,
      rating: 5,
      text: 'This template completely transformed how I manage my business finances. The automated summaries save me hours every month.',
      reviewer_name: 'Sarah Chen',
      date: '2026-02-15',
    },
    {
      id: 2,
      rating: 4,
      text: 'Very comprehensive and well-organized. Took a little time to set up initially but once everything was in place, it runs like a dream.',
      reviewer_name: 'Marcus Johnson',
      date: '2026-01-28',
    },
    {
      id: 3,
      rating: 5,
      text: 'Best budget tracker I have found. The charts are beautiful and the formulas are rock-solid. Highly recommend for any small business owner.',
      reviewer_name: 'Priya Patel',
      date: '2026-01-10',
    },
  ],
  creator: {
    name: 'TrackerSheets Team',
    avatar: '/images/avatars/trackerteam.png',
    slug: 'trackersheets-team',
  },
  downloads: 2847,
  rating: 4.8,
  rating_count: 156,
  category: 'Finance & Accounting',
  tags: ['budget', 'finance', 'accounting', 'business', 'expenses', 'income', 'forecasting'],
};

const MOCK_RELATED = [
  {
    id: 2,
    slug: 'personal-finance-dashboard',
    title: 'Personal Finance Dashboard',
    platform: 'google_sheets',
    price: 0,
    rating: 4.6,
    downloads: 5120,
    screenshot: '/images/templates/personal-finance.png',
    creator_name: 'TrackerSheets Team',
  },
  {
    id: 3,
    slug: 'project-cost-estimator',
    title: 'Project Cost Estimator',
    platform: 'excel',
    price: 7.99,
    rating: 4.5,
    downloads: 1340,
    screenshot: '/images/templates/cost-estimator.png',
    creator_name: 'SpreadsheetPro',
  },
  {
    id: 4,
    slug: 'invoice-generator-suite',
    title: 'Invoice Generator Suite',
    platform: 'google_sheets',
    price: 12.99,
    rating: 4.9,
    downloads: 3670,
    screenshot: '/images/templates/invoice-suite.png',
    creator_name: 'TrackerSheets Team',
  },
];

function PlatformBadge({ platform }) {
  const config = {
    google_sheets: {
      label: 'Google Sheets',
      classes: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    },
    excel: {
      label: 'Microsoft Excel',
      classes: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    },
  };
  const { label, classes } = config[platform] || config.google_sheets;
  return (
    <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${classes}`}>
      {label}
    </span>
  );
}

function StarRating({ rating, size = 16 }) {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    if (i <= Math.floor(rating)) {
      stars.push(
        <Star key={i} size={size} className="text-yellow-400 fill-yellow-400" />
      );
    } else if (i - rating < 1) {
      stars.push(
        <Star key={i} size={size} className="text-yellow-400 fill-yellow-400 opacity-50" />
      );
    } else {
      stars.push(
        <Star key={i} size={size} className="text-gray-300 dark:text-gray-600" />
      );
    }
  }
  return <div className="flex items-center gap-0.5">{stars}</div>;
}

export default function TemplateDetail() {
  const { templateSlug } = useParams();
  const [template, setTemplate] = useState(null);
  const [related, setRelated] = useState([]);
  const [currentImage, setCurrentImage] = useState(0);
  const [isFavorited, setIsFavorited] = useState(false);

  useEffect(() => {
    // TODO: Fetch template from Supabase using templateSlug
    // const fetchTemplate = async () => {
    //   const { data, error } = await supabase
    //     .from('templates')
    //     .select('*, creator:creators(*), reviews(*)')
    //     .eq('slug', templateSlug)
    //     .single();
    //   if (!error) setTemplate(data);
    // };
    // fetchTemplate();
    setTemplate(MOCK_TEMPLATE);
    setRelated(MOCK_RELATED);
  }, [templateSlug]);

  if (!template) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-gray-950">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  const prevImage = () =>
    setCurrentImage((prev) =>
      prev === 0 ? template.screenshots.length - 1 : prev - 1
    );
  const nextImage = () =>
    setCurrentImage((prev) =>
      prev === template.screenshots.length - 1 ? 0 : prev + 1
    );

  const renderDescription = (text) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('## ')) {
        return (
          <h2
            key={i}
            className="text-2xl font-bold mt-6 mb-3 text-gray-900 dark:text-white"
          >
            {line.replace('## ', '')}
          </h2>
        );
      }
      if (line.startsWith('### ')) {
        return (
          <h3
            key={i}
            className="text-xl font-semibold mt-5 mb-2 text-gray-900 dark:text-white"
          >
            {line.replace('### ', '')}
          </h3>
        );
      }
      if (line.trim() === '') return <br key={i} />;
      const formatted = line
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>');
      return (
        <p
          key={i}
          className="text-gray-700 dark:text-gray-300 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: formatted }}
        />
      );
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Link */}
        <Link
          to="/marketplace"
          className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 mb-6 transition-colors"
        >
          <ArrowLeft size={18} />
          <span className="font-medium">Back to Templates</span>
        </Link>

        {/* Two-Column Layout */}
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Left Column - Content */}
          <div className="w-full lg:w-2/3 space-y-8">
            {/* Screenshot Carousel */}
            <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-sm overflow-hidden border border-gray-200 dark:border-gray-800">
              <div className="aspect-video bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                <img
                  src={template.screenshots[currentImage]}
                  alt={`${template.title} screenshot ${currentImage + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `
                      <div class="flex flex-col items-center justify-center h-full text-gray-400 dark:text-gray-600">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span class="text-sm">Screenshot ${currentImage + 1} of ${template.screenshots.length}</span>
                      </div>
                    `;
                  }}
                />
              </div>
              {/* Carousel Controls */}
              <button
                onClick={prevImage}
                className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-2 rounded-full shadow-md hover:bg-white dark:hover:bg-gray-700 transition-colors"
                aria-label="Previous image"
              >
                <ChevronLeft size={20} className="text-gray-700 dark:text-gray-300" />
              </button>
              <button
                onClick={nextImage}
                className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-2 rounded-full shadow-md hover:bg-white dark:hover:bg-gray-700 transition-colors"
                aria-label="Next image"
              >
                <ChevronRight size={20} className="text-gray-700 dark:text-gray-300" />
              </button>
              {/* Carousel Dots */}
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                {template.screenshots.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentImage(idx)}
                    className={`w-2.5 h-2.5 rounded-full transition-colors ${
                      idx === currentImage
                        ? 'bg-blue-500'
                        : 'bg-white/60 dark:bg-gray-600/60 hover:bg-white dark:hover:bg-gray-500'
                    }`}
                    aria-label={`Go to image ${idx + 1}`}
                  />
                ))}
              </div>
            </div>

            {/* Description */}
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-800">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Description
              </h2>
              <div className="prose dark:prose-invert max-w-none">
                {renderDescription(template.description)}
              </div>
            </div>

            {/* Features List */}
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-800">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Features
              </h2>
              <ul className="space-y-3">
                {template.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Use Cases */}
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-800">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Use Cases
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {template.use_cases.map((useCase, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700"
                  >
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      {useCase.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {useCase.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Video Embed */}
            {template.video_url && (
              <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-800">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                  <Play size={24} />
                  Video Walkthrough
                </h2>
                <div className="aspect-video rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
                  <iframe
                    src={template.video_url}
                    title={`${template.title} video walkthrough`}
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>
            )}

            {/* Reviews */}
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-800">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Reviews
                </h2>
                <div className="flex items-center gap-2">
                  <StarRating rating={template.rating} />
                  <span className="text-gray-600 dark:text-gray-400 text-sm">
                    {template.rating} ({template.rating_count} reviews)
                  </span>
                </div>
              </div>
              <div className="space-y-6">
                {template.reviews.map((review) => (
                  <div
                    key={review.id}
                    className="border-b border-gray-100 dark:border-gray-800 pb-6 last:border-0 last:pb-0"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                          <User size={16} className="text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white text-sm">
                            {review.reviewer_name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-500">
                            {new Date(review.date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}
                          </p>
                        </div>
                      </div>
                      <StarRating rating={review.rating} size={14} />
                    </div>
                    <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed ml-12">
                      {review.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="w-full lg:w-1/3">
            <div className="lg:sticky lg:top-8 space-y-6">
              {/* Main Info Card */}
              <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm p-6 border border-gray-200 dark:border-gray-800">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                  {template.title}
                </h1>

                <div className="mb-4">
                  <PlatformBadge platform={template.platform} />
                </div>

                <div className="mb-6">
                  {template.price === 0 ? (
                    <span className="text-3xl font-bold text-green-600 dark:text-green-400">
                      Free
                    </span>
                  ) : (
                    <span className="text-3xl font-bold text-gray-900 dark:text-white">
                      ${template.price.toFixed(2)}
                    </span>
                  )}
                </div>

                <div className="space-y-3 mb-6">
                  <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
                    <Download size={18} />
                    Get This Template
                  </button>
                  <button className="w-full border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-blue-500 hover:text-blue-600 dark:hover:border-blue-400 dark:hover:text-blue-400 font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2">
                    <Eye size={18} />
                    Preview Template
                  </button>
                </div>

                {/* Creator Info */}
                <div className="border-t border-gray-100 dark:border-gray-800 pt-5 mb-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden flex items-center justify-center">
                      <img
                        src={template.creator.avatar}
                        alt={template.creator.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <User size={18} className="text-gray-500 dark:text-gray-400" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white text-sm">
                        {template.creator.name}
                      </p>
                      <Link
                        to={`/marketplace/creator/${template.creator.slug}`}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        View all templates
                      </Link>
                    </div>
                  </div>
                </div>

                {/* Template Stats */}
                <div className="border-t border-gray-100 dark:border-gray-800 pt-5 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400 flex items-center gap-2">
                      <Download size={14} />
                      Downloads
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {template.downloads.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400 flex items-center gap-2">
                      <Star size={14} />
                      Rating
                    </span>
                    <div className="flex items-center gap-1.5">
                      <StarRating rating={template.rating} size={13} />
                      <span className="font-medium text-gray-900 dark:text-white">
                        {template.rating}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 dark:text-gray-400 flex items-center gap-2">
                      <Tag size={14} />
                      Category
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {template.category}
                    </span>
                  </div>
                </div>

                {/* Tags */}
                <div className="border-t border-gray-100 dark:border-gray-800 pt-5 mt-5">
                  <div className="flex flex-wrap gap-2">
                    {template.tags.map((tag) => (
                      <Link
                        key={tag}
                        to={`/marketplace?tag=${tag}`}
                        className="px-3 py-1 text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-full hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/30 dark:hover:text-blue-400 transition-colors"
                      >
                        {tag}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Add to Favorites */}
                <div className="border-t border-gray-100 dark:border-gray-800 pt-5 mt-5">
                  <button
                    onClick={() => setIsFavorited(!isFavorited)}
                    className={`w-full py-2.5 px-4 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors ${
                      isFavorited
                        ? 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800'
                        : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400'
                    }`}
                  >
                    <Heart
                      size={18}
                      className={isFavorited ? 'fill-red-500 text-red-500' : ''}
                    />
                    {isFavorited ? 'Added to Favorites' : 'Add to Favorites'}
                  </button>
                </div>

                {/* Share Buttons */}
                <div className="border-t border-gray-100 dark:border-gray-800 pt-5 mt-5">
                  <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    Share
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      className="flex-1 py-2 px-3 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/30 dark:hover:text-blue-400 transition-colors flex items-center justify-center gap-1.5 text-sm"
                      title="Copy link"
                    >
                      <Share2 size={14} />
                      Link
                    </button>
                    <button
                      className="flex-1 py-2 px-3 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/30 dark:hover:text-blue-400 transition-colors flex items-center justify-center gap-1.5 text-sm"
                      title="Share on Twitter"
                    >
                      <ExternalLink size={14} />
                      Twitter
                    </button>
                    <button
                      className="flex-1 py-2 px-3 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/30 dark:hover:text-blue-400 transition-colors flex items-center justify-center gap-1.5 text-sm"
                      title="Share on LinkedIn"
                    >
                      <ExternalLink size={14} />
                      LinkedIn
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Related Templates */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
            Related Templates
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((item) => (
              <Link
                key={item.id}
                to={`/marketplace/template/${item.slug}`}
                className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-800 overflow-hidden hover:shadow-md transition-shadow group"
              >
                <div className="aspect-video bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                  <img
                    src={item.screenshot}
                    alt={item.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  <Eye
                    size={32}
                    className="text-gray-300 dark:text-gray-600 absolute"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors mb-1">
                    {item.title}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    by {item.creator_name}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <StarRating rating={item.rating} size={12} />
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {item.rating}
                      </span>
                    </div>
                    <span
                      className={`font-semibold text-sm ${
                        item.price === 0
                          ? 'text-green-600 dark:text-green-400'
                          : 'text-gray-900 dark:text-white'
                      }`}
                    >
                      {item.price === 0 ? 'Free' : `$${item.price.toFixed(2)}`}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
